import { useQuery } from '@tanstack/react-query';

import { readWorkspaceAiModels } from '@/lib/api/generated/api';
import type { WorkspaceAIModels } from '@/lib/api/generated/model';
import { aiModelsKeys } from '../queryKeys';

/**
 * A workspace's AI models for a period (service spec 169).
 *
 * Anything but a 200 throws rather than resolving to no entries, so a failed read
 * never reads as "no workflow here uses an AI model". There is no refusal to explain:
 * everyone who can open the workspace may read this, and the shell answers for those
 * who cannot.
 */
export function useWorkspaceAiModels(
  workspaceId: string,
  period: string | undefined,
) {
  return useQuery<WorkspaceAIModels>({
    queryKey: aiModelsKeys.workspace(workspaceId, period),
    queryFn: async ({ signal }) => {
      const response = await readWorkspaceAiModels(
        workspaceId,
        period === undefined ? {} : { period },
        { signal },
      );
      if (response.status === 200) return response.data;
      throw new Error('The AI models could not be read.');
    },
    // No polling, as on the Usage page: figures change when runs finish.
    refetchOnWindowFocus: false,
  });
}
